import { Link } from 'react-router-dom';
import { useEffect, useState, useMemo, useLayoutEffect, useRef, useCallback, type ElementType } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  HiLocationMarker, 
  HiMap, 
  HiUsers, 
  HiCalendar, 
  HiChatAlt2, 
  HiCheckCircle, 
  HiChartBar,
  HiSparkles,
  HiStar,
  HiDeviceMobile,
  HiChevronRight,
  HiChevronLeft,
  HiOfficeBuilding,
  HiUser,
  HiDesktopComputer
} from 'react-icons/hi';
import { IoTennisballSharp } from 'react-icons/io5';
import { FaGamepad } from 'react-icons/fa6';
import { MdRunCircle, MdSportsBar, MdStadium } from 'react-icons/md';
import { RiBoxingFill } from 'react-icons/ri';
import { IoMdSettings } from 'react-icons/io';
import { AppDispatch, RootState } from '@/ducks';
import { fetchPlaygroundsLimited } from '@/ducks/playgrounds';
import { Playground } from '@/types/playground';
import { getImageUrl } from '@/lib/api';
import { getSportRussianName } from '@/lib/sports';
import { useLanguage } from '@/contexts/LanguageContext';
import FaqAccordion from '@/components/common/FaqAccordion/FaqAccordion';
import MobileOnlyModal, {
  type MobileOnlyVariant,
} from '@/components/common/MobileOnlyModal/MobileOnlyModal';
import './Home.css';

const HOME_FAQ_IDS = [
  'whatIs',
  'sports',
  'cities',
  'newCoach',
  'rentOrSection',
  'findPartner',
  'meetings',
  'appFree',
] as const;

export default function Home() {
  const dispatch = useDispatch<AppDispatch>();
  const { t, language } = useLanguage();
  const { items: playgrounds, isLoading } = useSelector(
    (state: RootState) => state.playgrounds
  );
  const [featuredPlaygrounds, setFeaturedPlaygrounds] = useState<Playground[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeTab, setActiveTab] = useState<'athletes' | 'landlords'>('athletes');
  const [visibleTab, setVisibleTab] = useState<'athletes' | 'landlords'>('athletes');
  const [tabAnim, setTabAnim] = useState<'idle' | 'out'>('idle');
  const cardStackRef = useRef<HTMLDivElement>(null);
  const cardBodyRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const athletesTabRef = useRef<HTMLButtonElement>(null);
  const landlordsTabRef = useRef<HTMLButtonElement>(null);
  const [tabIndicator, setTabIndicator] = useState({ width: 0, left: 0 });
  const [hasTriedLoad, setHasTriedLoad] = useState(false);
  const [mobileOnlyOpen, setMobileOnlyOpen] = useState(false);
  const [mobileOnlyVariant, setMobileOnlyVariant] =
    useState<MobileOnlyVariant>('playgrounds');

  const openMobileOnly = useCallback((variant: MobileOnlyVariant) => {
    setMobileOnlyVariant(variant);
    setMobileOnlyOpen(true);
  }, []);

  const closeMobileOnly = useCallback(() => {
    setMobileOnlyOpen(false);
  }, []);

  const getSportLabel = useCallback((sport: { code?: string; id?: string | number; name?: string; russianName?: string }) => {
    return sport.name || sport.russianName || getSportRussianName(String(sport.code ?? sport.id ?? sport.name ?? ''));
  }, []);

  const requestTab = useCallback((tab: 'athletes' | 'landlords') => {
    if (tab === activeTab || tabAnim === 'out') return;
    setActiveTab(tab);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisibleTab(tab);
      setCurrentSlide(0);
      setTabAnim('idle');
      return;
    }

    setTabAnim('out');
  }, [activeTab, tabAnim]);

  useEffect(() => {
    if (tabAnim !== 'out') return;

    const timer = window.setTimeout(() => {
      setVisibleTab(activeTab);
      setCurrentSlide(0);
      setTabAnim('idle');
    }, 220);

    return () => window.clearTimeout(timer);
  }, [tabAnim, activeTab]);

  useEffect(() => {
    // Загружаем только 6 площадок для показа на лендинге
    // Загружаем только если еще не загружали и не загружаем сейчас
    if (playgrounds.length === 0 && !isLoading && !hasTriedLoad) {
      setHasTriedLoad(true);
      dispatch(fetchPlaygroundsLimited(6));
    }
    
    // Всегда обновляем featuredPlaygrounds (даже если массив пустой)
    if (playgrounds.length > 0) {
      // Показываем первые 6 площадок (приоритет тем, у которых есть изображения)
      const withImages = playgrounds
        .filter(p => p.images && p.images.length > 0)
        .slice(0, 6);
      
      // Если площадок с изображениями меньше 6, добавляем площадки без изображений
      if (withImages.length < 6) {
        const withoutImages = playgrounds
          .filter(p => !p.images || p.images.length === 0)
          .slice(0, 6 - withImages.length);
        setFeaturedPlaygrounds([...withImages, ...withoutImages]);
      } else {
        setFeaturedPlaygrounds(withImages);
      }
    } else {
      // Если площадок нет, очищаем featuredPlaygrounds
      setFeaturedPlaygrounds([]);
    }
  }, [dispatch, playgrounds.length, isLoading, hasTriedLoad, playgrounds]);

  // Данные слайдов с иконками (с переводами)
  const slides = useMemo(() => [
    {
      image: '/images/mockup-map%20Large.jpeg',
      title: t('home.slides.allPlaygrounds.title'),
      description: t('home.slides.allPlaygrounds.description'),
      features: [
        { icon: MdStadium, text: t('home.slides.allPlaygrounds.features.sportsComplexes') },
        { icon: HiMap, text: t('home.slides.allPlaygrounds.features.interactiveMap') },
        { icon: IoMdSettings, text: t('home.slides.allPlaygrounds.features.filters') }
      ]
    },
    {
      image: '/images/mockup-meeting%20Large.jpeg',
      title: t('home.slides.createMeetings.title'),
      description: t('home.slides.createMeetings.description'),
      features: [
        { icon: HiUsers, text: t('home.slides.createMeetings.features.findPartners') },
        { icon: HiCalendar, text: t('home.slides.createMeetings.features.planTraining') },
        { icon: HiChatAlt2, text: t('home.slides.createMeetings.features.communicate') }
      ]
    },
    {
      image: '/images/mockup-checkin%20Large.jpeg',
      title: t('home.slides.checkins.title'),
      description: t('home.slides.checkins.description'),
      features: [
        { icon: HiCheckCircle, text: t('home.slides.checkins.features.checkIn') },
        { icon: HiLocationMarker, text: t('home.slides.checkins.features.findActive') }
      ]
    },
    {
      image: '/images/mockup-foc%20Large.jpeg',
      title: t('home.slides.focServices.title'),
      description: t('home.slides.focServices.description'),
      features: [
        { icon: HiCalendar, text: t('home.slides.focServices.features.bookings') },
        { icon: HiUsers, text: t('home.slides.focServices.features.sections') }
      ]
    },
    {
      image: '/images/mockup-sports%20Large.jpeg',
      title: t('home.slides.sportsTypes.title'),
      description: t('home.slides.sportsTypes.description'),
      features: [
        { icon: IoTennisballSharp, text: t('home.slides.sportsTypes.features.gameSports') },
        { icon: FaGamepad, text: t('home.slides.sportsTypes.features.esports') },
        { icon: MdRunCircle, text: t('home.slides.sportsTypes.features.running') },
        { icon: RiBoxingFill, text: t('home.slides.sportsTypes.features.combat') },
        { icon: MdSportsBar, text: t('home.slides.sportsTypes.features.other') }
      ]
    }
  ], [t]);

  const landlordSlides = useMemo(() => [
    {
      title: t('home.landlordSlides.rental.title'),
      description: t('home.landlordSlides.rental.description'),
      features: [
        { icon: HiCalendar, text: t('home.landlordSlides.rental.features.schedule') },
        { icon: HiCheckCircle, text: t('home.landlordSlides.rental.features.bookings') },
        { icon: HiMap, text: t('home.landlordSlides.rental.features.timeline') },
      ],
    },
    {
      title: t('home.landlordSlides.sections.title'),
      description: t('home.landlordSlides.sections.description'),
      features: [
        { icon: HiStar, text: t('home.landlordSlides.sections.features.subscriptions') },
        { icon: HiUser, text: t('home.landlordSlides.sections.features.clientCards') },
        { icon: HiDeviceMobile, text: t('home.landlordSlides.sections.features.appBooking') },
        { icon: HiDesktopComputer, text: t('home.landlordSlides.sections.features.offlineBooking') },
      ],
    },
    {
      title: t('home.landlordSlides.reports.title'),
      description: t('home.landlordSlides.reports.description'),
      features: [
        { icon: HiChartBar, text: t('home.landlordSlides.reports.features.revenue') },
        { icon: HiCalendar, text: t('home.landlordSlides.reports.features.occupancy') },
        { icon: HiUsers, text: t('home.landlordSlides.reports.features.clients') },
        { icon: HiSparkles, text: t('home.landlordSlides.reports.features.network') },
      ],
    },
    {
      title: t('home.landlordSlides.brandedApp.title'),
      description: t('home.landlordSlides.brandedApp.description'),
      features: [
        { icon: HiDeviceMobile, text: t('home.landlordSlides.brandedApp.features.customApp') },
      ],
    },
  ], [t]);

  useEffect(() => {
    const slideCount = visibleTab === 'athletes' ? slides.length : landlordSlides.length;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === slideCount - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [visibleTab, slides.length, landlordSlides.length]);

  const syncCardHeight = useCallback(() => {
    const body = cardBodyRef.current;
    const stack = cardStackRef.current;
    if (!body || !stack) return;

    const active = body.querySelector('.app-slide-info-content.active') as HTMLElement | null;
    if (active) {
      stack.style.height = `${active.offsetHeight}px`;
    }
  }, []);

  const updateTabIndicator = useCallback(() => {
    const activeBtn = activeTab === 'athletes' ? athletesTabRef.current : landlordsTabRef.current;
    const tabs = tabsRef.current;
    if (!activeBtn || !tabs) return;

    const tabsRect = tabs.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    setTabIndicator({
      width: btnRect.width,
      left: btnRect.left - tabsRect.left,
    });
  }, [activeTab]);

  useLayoutEffect(() => {
    updateTabIndicator();
  }, [updateTabIndicator, t, language]);

  useLayoutEffect(() => {
    syncCardHeight();
  }, [currentSlide, visibleTab, slides, landlordSlides, syncCardHeight, tabAnim]);

  useEffect(() => {
    const onResize = () => {
      syncCardHeight();
      updateTabIndicator();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [syncCardHeight, updateTabIndicator]);

  const adminUrl = import.meta.env.PROD
    ? 'https://admin.sportsground.ru'
    : 'http://localhost:3000';
  const adminRegistrationUrl = import.meta.env.PROD
    ? 'https://admin.sportsground.ru/registration'
    : 'http://localhost:3000/registration';
  const appStoreUrl = 'https://apps.apple.com/tr/app/playbase/id6754344676';
  const googlePlayUrl =
    'https://play.google.com/store/apps/details?id=com.sportsground.playbase&pli=1';

  const heroStats = [
    { icon: HiOfficeBuilding, value: '16000+', label: t('home.playgroundsCount') },
    { icon: HiUsers, value: '20 000+', label: t('home.activeUsers') },
    { icon: HiCalendar, value: '30+', label: t('home.sportsTypes') },
    { icon: HiLocationMarker, value: '50+', label: t('home.citiesCount') },
  ];

  const heroAvatars = [
    '/images/hero/avatars/avatar-1.jpg',
    '/images/hero/avatars/avatar-2.jpg',
    '/images/hero/avatars/avatar-3.jpg',
    '/images/hero/avatars/avatar-4.jpg',
  ];

  type SliderFeature = { icon: ElementType<{ className?: string }>; text: string };
  type SliderItem = { title: string; description: string; features: SliderFeature[]; image?: string };

  const getPlaygroundImage = (playground: Playground) => {
    if (playground.thumbnail) {
      return getImageUrl(playground.thumbnail);
    }
    if (playground.images && playground.images.length > 0) {
      return getImageUrl(playground.images[0]);
    }
    return null;
  };

  const formatFeaturedDistance = (distance?: number) => {
    if (!distance) return null;
    if (distance < 1000) {
      return t('home.featured.meters').replace('{distance}', String(Math.round(distance)));
    }
    return t('home.featured.kilometers').replace('{distance}', (distance / 1000).toFixed(1));
  };

  const formatFeaturedPrice = (price?: number) => {
    if (!price || price === 0) return null;
    return t('home.featured.fromPrice').replace('{price}', String(price * 2));
  };

  const renderSlidePanels = (slideList: SliderItem[], tabKey: string) =>
    slideList.map((slide, index) => (
      <div
        key={`${tabKey}-${slide.title}`}
        className={`app-slide-info-content ${currentSlide === index ? 'active' : ''}`}
        aria-hidden={currentSlide !== index}
      >
        <h3 className="app-slide-title">{slide.title}</h3>
        <p className="app-slide-description">{slide.description}</p>
        <ul className="app-slide-features">
          {slide.features.map((feature, idx) => (
            <li key={idx}>
              <feature.icon className="app-slide-feature-icon" aria-hidden="true" />
              <span>{feature.text}</span>
            </li>
          ))}
        </ul>
      </div>
    ));

  const renderSlideControls = (slideList: SliderItem[]) => (
    <>
      <button
        type="button"
        className="app-slider-control-btn"
        onClick={() =>
          setCurrentSlide((prev) => (prev === 0 ? slideList.length - 1 : prev - 1))
        }
        aria-label={t('home.appSlider.prevSlideAria')}
      >
        <HiChevronLeft aria-hidden="true" />
      </button>

      <div className="app-slider-progress" role="tablist" aria-label={t('home.appSlider.progressAria')}>
        {slideList.map((slide, index) => (
          <button
            key={slide.title}
            type="button"
            role="tab"
            aria-selected={currentSlide === index}
            aria-label={slide.title}
            className={`app-slider-progress-dot ${currentSlide === index ? 'active' : ''}`}
            onClick={() => setCurrentSlide(index)}
          />
        ))}
      </div>

      <button
        type="button"
        className="app-slider-control-btn"
        onClick={() =>
          setCurrentSlide((prev) => (prev === slideList.length - 1 ? 0 : prev + 1))
        }
        aria-label={t('home.appSlider.nextSlideAria')}
      >
        <HiChevronRight aria-hidden="true" />
      </button>
    </>
  );

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="landing-hero landing-hero-v2">
        <div className="hero-v2-stage">
          <div className="container hero-v2-container">
            <div className="hero-v2-visual">
              <img
                src="/images/background-image.png"
                alt={t('home.hero.visualAlt')}
                className="hero-v2-visual-image"
              />
            </div>

            <div className="hero-v2-copy">
              <span className="hero-v2-badge">
                <span className="hero-v2-badge-dot" aria-hidden="true" />
                {t('home.hero.badge')}
              </span>

              <h1 className="hero-v2-title">
                {t('home.heroTitle')}{' '}
                <span className="hero-v2-title-accent">{t('home.heroTitleAccent')}</span>
              </h1>

              <p className="hero-v2-lead">{t('home.hero.lead')}</p>

              <div className="hero-v2-audience">
                <button
                  type="button"
                  className="hero-v2-audience-card"
                  onClick={() => openMobileOnly('playgrounds')}
                >
                  <span className="hero-v2-audience-icon hero-v2-audience-icon-athlete">
                    <HiUser aria-hidden="true" />
                  </span>
                  <span className="hero-v2-audience-text">
                    <span className="hero-v2-audience-title">{t('home.hero.athleteCard.title')}</span>
                    <span className="hero-v2-audience-link">
                      {t('home.hero.athleteCard.link')}
                      <HiChevronRight aria-hidden="true" />
                    </span>
                  </span>
                </button>

                <a
                  href={adminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hero-v2-audience-card"
                >
                  <span className="hero-v2-audience-icon hero-v2-audience-icon-facility">
                    <MdStadium aria-hidden="true" />
                  </span>
                  <span className="hero-v2-audience-text">
                    <span className="hero-v2-audience-title">{t('home.hero.facilityCard.title')}</span>
                    <span className="hero-v2-audience-link">
                      {t('home.hero.facilityCard.link')}
                      <HiChevronRight aria-hidden="true" />
                    </span>
                  </span>
                </a>
              </div>
            </div>

            <div className="hero-v2-stats">
              {heroStats.map(({ icon: Icon, value, label }) => (
                <div key={label} className="hero-v2-stat">
                  <span className="hero-v2-stat-icon">
                    <Icon aria-hidden="true" />
                  </span>
                  <div className="hero-v2-stat-copy">
                    <div className="hero-v2-stat-value">{value}</div>
                    <div className="hero-v2-stat-label">{label}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="hero-v2-bottom">
              <div className="hero-v2-bottom-left">
                <p className="hero-v2-join">{t('home.hero.joinCommunity')}</p>
                <div className="hero-v2-store-buttons">
                  <a
                    href={appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hero-v2-store-btn hero-v2-store-btn-apple"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                    <span>{t('common.appStore')}</span>
                  </a>
                  <a
                    href={googlePlayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hero-v2-store-btn hero-v2-store-btn-google"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    <span>{t('common.googlePlay')}</span>
                  </a>
                </div>
              </div>

              <div className="hero-v2-social">
                <div className="hero-v2-avatars">
                  {heroAvatars.map((src) => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      className="hero-v2-avatar"
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </div>
                <p className="hero-v2-social-text">{t('home.hero.socialProof')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Screens Slider Section */}
      <section className="landing-app-slider">
        <div className="container">
          <div className="app-slider-panel">
            <div className="app-slider-container">
              <div
                className={`app-slider-content${visibleTab === 'landlords' ? ' app-slider-content--landlords' : ''}${tabAnim === 'out' ? ' is-tab-out' : ''}`}
              >
                <div className="app-slider-switchable app-slider-switchable--visual">
                  <div
                    className={`app-slider-visual ${visibleTab === 'landlords' ? 'app-slider-visual--landlords' : ''}`}
                  >
                    {visibleTab === 'athletes' ? (
                      <div className="phone-mockup-wrap">
                        {slides.map((slide, index) => (
                          <img
                            key={slide.image}
                            src={slide.image}
                            alt={slide.title}
                            className={`phone-mockup-image ${currentSlide === index ? 'is-active' : ''}`}
                            draggable={false}
                            aria-hidden={currentSlide !== index}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/images/phone-mockup.png';
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="laptop-mockup-wrap">
                        <img
                          src="/images/admin-interface.png"
                          alt={language === 'ru' ? 'Интерфейс админ-панели' : 'Admin panel interface'}
                          className="laptop-mockup-image"
                          draggable={false}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={`app-slider-info ${visibleTab === 'landlords' ? 'app-slider-info--landlords' : ''}`}
                >
                  <div className="app-slider-header">
                    <div className="app-slider-tabs" ref={tabsRef}>
                      <span
                        className="app-slider-tab-indicator"
                        style={{
                          width: tabIndicator.width,
                          transform: `translateX(${tabIndicator.left}px)`,
                        }}
                        aria-hidden="true"
                      />
                      <button
                        ref={athletesTabRef}
                        type="button"
                        className={`app-slider-tab ${activeTab === 'athletes' ? 'active' : ''}`}
                        onClick={() => requestTab('athletes')}
                      >
                        {t('home.forAthletes')}
                      </button>
                      <button
                        ref={landlordsTabRef}
                        type="button"
                        className={`app-slider-tab ${activeTab === 'landlords' ? 'active' : ''}`}
                        onClick={() => requestTab('landlords')}
                      >
                        {t('home.forFacilities')}
                      </button>
                    </div>
                  </div>

                  <div className="app-slider-switchable app-slider-switchable--panel">
                    <p className="app-slider-subtitle">
                      {visibleTab === 'athletes'
                        ? t('home.appSubtitle')
                        : t('home.appSlider.landlordsSubtitle')}
                    </p>

                    <div className="app-slider-card">
                      <div className="app-slider-card-stack" ref={cardStackRef}>
                        <div className="app-slider-card-body" ref={cardBodyRef}>
                          {renderSlidePanels(
                            visibleTab === 'athletes' ? slides : landlordSlides,
                            visibleTab,
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="app-slider-controls" aria-label={t('home.appSlider.controlsAria')}>
                      {renderSlideControls(visibleTab === 'athletes' ? slides : landlordSlides)}
                    </div>
                  </div>

                  {visibleTab === 'landlords' ? (
                    <a
                      href={adminUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`btn btn-primary app-slider-panel-cta ${tabAnim === 'idle' ? 'is-visible' : ''}`}
                      tabIndex={tabAnim === 'idle' ? 0 : -1}
                    >
                      {t('common.enterAdmin')}
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="landing-video" id="how-it-works">
        <div className="container">
          <p className="section-subtitle landing-video-subtitle">
            {t('home.howItWorks.videoSubtitle')}
          </p>
          <div className="video-wrapper">
            <div className="video-container">
              <iframe
                src="https://www.youtube.com/embed/-ilKx73QmYQ"
                title={language === 'ru' ? 'Видео о сервисе Площадка' : 'Playbase service video'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-iframe"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Playgrounds Section */}
      <section className="landing-featured" id="venues">
        <div className="container">
          <div className="featured-head">
            <div className="featured-head-copy">
              <h2 className="featured-title">{t('home.featured.title')}</h2>
              <p className="featured-subtitle">{t('home.featured.subtitle')}</p>
            </div>
            {!isLoading && featuredPlaygrounds.length > 0 ? (
              <button
                type="button"
                className="featured-head-link"
                onClick={() => openMobileOnly('playgrounds')}
              >
                {t('home.featured.viewAll')}
                <HiChevronRight aria-hidden="true" />
              </button>
            ) : null}
          </div>

          {isLoading ? (
            <div className="featured-rail" aria-hidden="true">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="featured-card featured-card--skeleton" />
              ))}
            </div>
          ) : null}

          {!isLoading && featuredPlaygrounds.length === 0 && playgrounds.length === 0 ? (
            <div className="featured-empty">
              <p>{t('home.featured.empty')}</p>
              <button
                type="button"
                className="featured-head-link"
                onClick={() => openMobileOnly('playgrounds')}
              >
                {t('home.featured.viewAll')}
                <HiChevronRight aria-hidden="true" />
              </button>
            </div>
          ) : null}

          {!isLoading && featuredPlaygrounds.length > 0 ? (
            <div className="featured-rail-wrap">
              <div className="featured-rail">
                {featuredPlaygrounds.map((playground) => {
                  const imageUrl = getPlaygroundImage(playground);
                  const primarySport = playground.sports?.[0];
                  const sportLabel = primarySport ? getSportLabel(primarySport) : null;
                  const distanceLabel = formatFeaturedDistance(playground.distance);
                  const priceLabel = formatFeaturedPrice(playground.price);
                  const locationLine = [playground.address, playground.city].filter(Boolean).join(', ');

                  return (
                    <button
                      key={playground.id}
                      type="button"
                      className="featured-card"
                      onClick={() => openMobileOnly('playgroundCard')}
                    >
                      <div className="featured-card-media">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={playground.name}
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                'https://images.unsplash.com/photo-1576678927484-cc907957088c?w=400&h=300&fit=crop';
                            }}
                          />
                        ) : (
                          <div className="featured-card-placeholder">
                            <span>{t('home.featured.noImage')}</span>
                          </div>
                        )}
                        {playground.rate && playground.rate > 0 ? (
                          <div className="featured-card-rating">
                            <HiStar className="rating-star" aria-hidden="true" />
                            <span className="rating-value">{playground.rate.toFixed(1)}</span>
                          </div>
                        ) : null}
                      </div>

                      <div className="featured-card-body">
                        <h3 className="featured-card-title">{playground.name}</h3>
                        {locationLine || distanceLabel ? (
                          <p className="featured-card-location">
                            <HiLocationMarker aria-hidden="true" />
                            <span>
                              {[locationLine, distanceLabel].filter(Boolean).join(' • ')}
                            </span>
                          </p>
                        ) : null}
                        <div className="featured-card-footer">
                          {sportLabel ? (
                            <span className="featured-card-sport">{sportLabel}</span>
                          ) : (
                            <span />
                          )}
                          {priceLabel ? (
                            <span className="featured-card-price">{priceLabel}</span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* Arcade mini-game entry */}
      <section className="landing-arcade" id="arcade">
        <div className="container">
          <div className="arcade-banner">
            <div className="arcade-copy">
              <span className="arcade-eyebrow">{t('home.arcade.eyebrow')}</span>
              <h2 className="arcade-title">{t('home.arcade.title')}</h2>
              <p className="arcade-description">{t('home.arcade.description')}</p>
              <a href="/game/index.html" className="arcade-cta">
                {t('home.arcade.cta')}
                <HiChevronRight aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Landlords Section */}
      <section className="landing-landlords" id="for-business">
        <div className="container">
          <div className="landlords-banner">
            <div className="landlords-card">
            <div className="landlords-card-copy">
              <h2 className="landlords-card-title">{t('home.landlords.title')}</h2>
              <p className="landlords-card-subtitle">{t('home.landlords.subtitle')}</p>
              <div className="landlords-card-actions">
                <a
                  href={adminRegistrationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="landlords-card-cta landlords-card-cta--primary"
                >
                  {t('home.landlords.connectCta')}
                  <HiChevronRight aria-hidden="true" />
                </a>
                <Link to="/for-landlords" className="landlords-card-cta landlords-card-cta--secondary">
                  {t('forLandlords.learnMore')}
                </Link>
                <a
                  href={adminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="landlords-card-cta landlords-card-cta--secondary"
                >
                  {t('common.enterAdmin')}
                </a>
              </div>
            </div>

            <img
              src="/images/landlords.jpg"
              alt={t('home.landlords.imageAlt')}
              className="landlords-card-image"
              loading="lazy"
            />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="landing-faq" id="faq">
        <div className="container">
          <FaqAccordion
            titleKey="home.faq.title"
            namespace="home.faq"
            itemIds={[...HOME_FAQ_IDS]}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="container">
          <div className="cta-banner">
            <div className="cta-banner-main">
              <div className="cta-banner-copy">
                <h2 className="cta-banner-title">{t('home.cta.title')}</h2>
                <p className="cta-banner-description">{t('home.cta.description')}</p>
              </div>
              <div className="cta-banner-visual">
                <img
                  src="/images/footer-phone.png"
                  alt={t('home.cta.phonesAlt')}
                  className="cta-banner-image"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="cta-banner-stores">
              <a
                href={appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-store-btn"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <span className="cta-store-btn-text">
                  <span className="cta-store-btn-line1">{t('home.cta.appStoreLine1')}</span>
                  <span className="cta-store-btn-line2">{t('common.appStore')}</span>
                </span>
              </a>
              <a
                href={googlePlayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-store-btn"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <span className="cta-store-btn-text">
                  <span className="cta-store-btn-line1">{t('home.cta.googlePlayLine1')}</span>
                  <span className="cta-store-btn-line2">{t('common.googlePlay')}</span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <MobileOnlyModal
        open={mobileOnlyOpen}
        onClose={closeMobileOnly}
        variant={mobileOnlyVariant}
      />
    </div>
  );
}






